'use client';

import React, { useState } from 'react';
import { Plus, Search, AlertTriangle, CheckCircle, XCircle, Clock, Eye, X, TrendingDown, TrendingUp, Minus, FileText, RefreshCw,  } from 'lucide-react';
import { FARMS, HOUSES_BY_FARM, MOCK_FLOCK_SUMMARIES } from '../../components/birdRecordsData';

// ─── Types ────────────────────────────────────────────────────────────────────

type ReconciliationStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'adjustment_posted';

interface ReconciliationRecord {
  id: string;
  reconciliationNumber: string;
  countDate: string;
  farm: string;
  house: string;
  flock: string;
  flockNumber: string;
  birdType: string;
  breed: string;
  systemPopulation: number;
  physicalCount: number;
  variance: number;
  variancePct: number;
  countedBy: string;
  verifiedBy?: string;
  approvedBy?: string;
  reason?: string;
  notes?: string;
  status: ReconciliationStatus;
  adjustmentPosted?: boolean;
  createdAt: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_RECONCILIATIONS: ReconciliationRecord[] = [
  {
    id: 'rec-001',
    reconciliationNumber: 'REC-2026-000001',
    countDate: '2026-08-31',
    farm: 'Idudi Poultry Farm',
    house: 'House A1',
    flock: 'Batch A1 — Cobb 500',
    flockNumber: 'BR-2026-001',
    birdType: 'Broiler',
    breed: 'Cobb 500',
    systemPopulation: 4820,
    physicalCount: 4812,
    variance: -8,
    variancePct: -0.17,
    countedBy: 'James Okello',
    verifiedBy: 'Sarah Namukasa',
    approvedBy: 'Farm Manager',
    reason: 'Unrecorded mortality — birds found dead in corner during count',
    notes: 'Mortality records updated separately',
    status: 'adjustment_posted',
    adjustmentPosted: true,
    createdAt: '2026-08-31T16:00:00Z',
  },
  {
    id: 'rec-002',
    reconciliationNumber: 'REC-2026-000002',
    countDate: '2026-08-31',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    flock: 'Layers Batch L1 — Lohmann',
    flockNumber: 'LY-2026-001',
    birdType: 'Layer',
    breed: 'Lohmann Brown',
    systemPopulation: 7748,
    physicalCount: 7742,
    variance: -6,
    variancePct: -0.08,
    countedBy: 'Moses Wafula',
    verifiedBy: 'Sarah Namukasa',
    approvedBy: 'Farm Manager',
    reason: 'Minor mortality not yet recorded in system',
    status: 'approved',
    createdAt: '2026-08-31T17:00:00Z',
  },
  {
    id: 'rec-003',
    reconciliationNumber: 'REC-2026-000003',
    countDate: '2026-09-04',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    flock: 'Batch A2 — Ross 308',
    flockNumber: 'BR-2026-002',
    birdType: 'Broiler',
    breed: 'Ross 308',
    systemPopulation: 4620,
    physicalCount: 4614,
    variance: -6,
    variancePct: -0.13,
    countedBy: 'James Okello',
    reason: 'Pending investigation',
    status: 'pending_approval',
    createdAt: '2026-09-04T14:00:00Z',
  },
  {
    id: 'rec-004',
    reconciliationNumber: 'REC-2026-000004',
    countDate: '2026-09-05',
    farm: 'Kampala Demo Farm',
    house: 'House C1',
    flock: 'Batch C1 — Arbor Acres',
    flockNumber: 'BR-2026-004',
    birdType: 'Broiler',
    breed: 'Arbor Acres',
    systemPopulation: 5974,
    physicalCount: 5974,
    variance: 0,
    variancePct: 0,
    countedBy: 'Peter Ssemakula',
    verifiedBy: 'Peter Ssemakula',
    status: 'approved',
    createdAt: '2026-09-05T09:00:00Z',
  },
  {
    id: 'rec-005',
    reconciliationNumber: 'REC-2026-000005',
    countDate: '2026-09-06',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 02',
    flock: 'Layers Batch L2 — Isa Brown',
    flockNumber: 'LY-2026-002',
    birdType: 'Layer',
    breed: 'ISA Brown',
    systemPopulation: 6325,
    physicalCount: 6318,
    variance: -7,
    variancePct: -0.11,
    countedBy: 'Moses Wafula',
    status: 'draft',
    createdAt: '2026-09-06T08:00:00Z',
  },
];

const VARIANCE_REASONS = [
  'Unrecorded mortality',
  'Recording error — mortality already occurred',
  'Birds escaped / missing',
  'Transfer not yet recorded',
  'Counting error — recount required',
  'System data entry error',
  'Birds found dead during count',
  'Other — see notes',
];

// ─── Status Config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<ReconciliationStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  draft: { label: 'Draft', color: '#64748b', bg: '#f1f5f9', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: '#d97706', bg: '#fef3c7', icon: Clock },
  approved: { label: 'Approved', color: '#2563eb', bg: '#dbeafe', icon: CheckCircle },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', icon: XCircle },
  adjustment_posted: { label: 'Adjustment Posted', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
};

// ─── New Count Form ───────────────────────────────────────────────────────────

interface NewCountFormProps {
  onClose: () => void;
  onSubmit: (r: Partial<ReconciliationRecord>) => void;
}

function NewCountForm({ onClose, onSubmit }: NewCountFormProps) {
  const [form, setForm] = useState({
    farm: FARMS[0],
    house: '',
    flockNumber: '',
    physicalCount: '',
    countedBy: '',
    reason: '',
    notes: '',
  });

  const houses = HOUSES_BY_FARM[form.farm] || [];
  const flocks = MOCK_FLOCK_SUMMARIES.filter(f => f.farm === form.farm && (!form.house || f.house === form.house));
  const selectedFlock = MOCK_FLOCK_SUMMARIES.find(f => f.flockNumber === form.flockNumber);
  const systemPop = selectedFlock?.currentBirds ?? 0;
  const physCount = Number(form.physicalCount) || 0;
  const variance = physCount > 0 ? physCount - systemPop : null;
  const variancePct = variance !== null && systemPop > 0 ? (variance / systemPop) * 100 : null;

  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFlock) return;
    onSubmit({
      farm: form.farm,
      house: form.house,
      flock: selectedFlock.flockName,
      flockNumber: form.flockNumber,
      birdType: selectedFlock.birdType,
      breed: selectedFlock.breed,
      systemPopulation: systemPop,
      physicalCount: physCount,
      variance: variance ?? 0,
      variancePct: variancePct ?? 0,
      countedBy: form.countedBy,
      reason: form.reason || undefined,
      notes: form.notes || undefined,
      status: 'draft',
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">New Physical Count</h2>
            <p className="text-xs text-gray-500 mt-0.5">Record actual bird count for reconciliation</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"><X size={16} className="text-gray-500" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Location */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Location & Flock</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Farm *</label>
                <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.farm} onChange={e => set('farm', e.target.value)} required>
                  {FARMS.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">House *</label>
                <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.house} onChange={e => set('house', e.target.value)} required>
                  <option value="">Select house</option>
                  {houses.map(h => <option key={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Flock *</label>
                <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.flockNumber} onChange={e => set('flockNumber', e.target.value)} required>
                  <option value="">Select flock</option>
                  {flocks.map(f => <option key={f.flockNumber} value={f.flockNumber}>{f.flockNumber} — {f.flockName}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Count */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Count Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 font-medium mb-1">System Population</p>
                <p className="text-2xl font-semibold text-blue-900">{systemPop > 0 ? systemPop.toLocaleString() : '—'}</p>
                {selectedFlock && <p className="text-xs text-blue-600 mt-0.5">{selectedFlock.birdType} · {selectedFlock.breed}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Physical Count *</label>
                <input type="number" min="0" className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" placeholder="Enter actual count" value={form.physicalCount} onChange={e => set('physicalCount', e.target.value)} required />
              </div>
            </div>

            {variance !== null && physCount > 0 && (
              <div className={`mt-3 p-3 rounded-lg flex items-start gap-2 text-xs ${variance === 0 ? 'bg-green-50 text-green-700' : variance < 0 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                {variance === 0 ? <CheckCircle size={14} className="flex-shrink-0 mt-0.5" /> : <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />}
                <div>
                  <p className="font-semibold">
                    {variance === 0 ? 'No variance — counts match' : `Variance: ${variance > 0 ? '+' : ''}${variance} birds (${variancePct?.toFixed(2)}%)`}
                  </p>
                  {variance !== 0 && <p className="mt-0.5 opacity-80">A reason is required before this reconciliation can be approved.</p>}
                </div>
              </div>
            )}
          </div>

          {/* Staff */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Staff & Notes</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Counted By *</label>
                <input className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" placeholder="Staff name" value={form.countedBy} onChange={e => set('countedBy', e.target.value)} required />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Variance Reason {variance !== 0 && variance !== null ? '*' : ''}</label>
                <select className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30" value={form.reason} onChange={e => set('reason', e.target.value)} required={variance !== null && variance !== 0}>
                  <option value="">Select reason</option>
                  {VARIANCE_REASONS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows={2} className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none" placeholder="Additional notes..." value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="button" onClick={() => { handleSubmit({ preventDefault: () => {} } as React.FormEvent); }} className="px-4 py-2 text-xs font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">Save Draft</button>
            <button type="submit" className="px-4 py-2 text-xs font-medium text-white rounded-lg transition-colors" style={{ backgroundColor: 'var(--primary)' }}>Submit for Approval</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────

interface ReconciliationDetailProps {
  record: ReconciliationRecord;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onPostAdjustment: (id: string) => void;
}

function ReconciliationDetail({ record, onClose, onApprove, onReject, onPostAdjustment }: ReconciliationDetailProps) {
  const cfg = STATUS_CONFIG[record.status];
  const StatusIcon = cfg.icon;
  const variance = record.variance;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{record.reconciliationNumber}</h2>
            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ color: cfg.color, backgroundColor: cfg.bg }}>
              <StatusIcon size={11} />{cfg.label}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"><X size={16} className="text-gray-500" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Population Comparison */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xs text-blue-600 font-medium">System Count</p>
              <p className="text-xl font-semibold text-blue-900 mt-1">{record.systemPopulation.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-xs text-green-600 font-medium">Physical Count</p>
              <p className="text-xl font-semibold text-green-900 mt-1">{record.physicalCount.toLocaleString()}</p>
            </div>
            <div className={`p-3 rounded-lg text-center ${variance === 0 ? 'bg-gray-50' : variance < 0 ? 'bg-red-50' : 'bg-amber-50'}`}>
              <p className={`text-xs font-medium ${variance === 0 ? 'text-gray-600' : variance < 0 ? 'text-red-600' : 'text-amber-600'}`}>Variance</p>
              <p className={`text-xl font-semibold mt-1 ${variance === 0 ? 'text-gray-900' : variance < 0 ? 'text-red-700' : 'text-amber-700'}`}>
                {variance === 0 ? '0' : (variance > 0 ? '+' : '') + variance}
              </p>
              {variance !== 0 && <p className={`text-xs mt-0.5 ${variance < 0 ? 'text-red-500' : 'text-amber-500'}`}>{record.variancePct.toFixed(2)}%</p>}
            </div>
          </div>

          {/* Variance Alert */}
          {variance !== 0 && (
            <div className={`p-3 rounded-lg flex items-start gap-2 text-xs ${variance < 0 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{variance < 0 ? 'Deficit' : 'Surplus'}: {Math.abs(variance)} birds ({Math.abs(record.variancePct).toFixed(2)}%)</p>
                {record.reason && <p className="mt-0.5 opacity-80">Reason: {record.reason}</p>}
              </div>
            </div>
          )}

          {/* Details */}
          <div className="space-y-2 text-xs">
            {[
              { label: 'Farm', value: record.farm },
              { label: 'House', value: record.house },
              { label: 'Flock', value: `${record.flockNumber} — ${record.flock}` },
              { label: 'Bird Type', value: record.birdType },
              { label: 'Breed', value: record.breed },
              { label: 'Count Date', value: record.countDate },
              { label: 'Counted By', value: record.countedBy },
              { label: 'Verified By', value: record.verifiedBy || '—' },
              { label: 'Approved By', value: record.approvedBy || '—' },
              { label: 'Notes', value: record.notes || '—' },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between py-1 border-b border-gray-50">
                <span className="text-gray-500">{label}</span>
                <span className="font-medium text-gray-900 text-right max-w-[60%]">{value}</span>
              </div>
            ))}
          </div>

          {/* Workflow Timeline */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Workflow</p>
            <div className="space-y-2">
              {[
                { step: 'Count Recorded', done: true, by: record.countedBy, date: record.countDate },
                { step: 'Submitted for Approval', done: ['pending_approval','approved','rejected','adjustment_posted'].includes(record.status), by: record.countedBy },
                { step: 'Approved / Rejected', done: ['approved','rejected','adjustment_posted'].includes(record.status), by: record.approvedBy },
                { step: 'Adjustment Posted', done: record.status === 'adjustment_posted', by: record.approvedBy },
              ].map(({ step, done, by, date }) => (
                <div key={step} className="flex items-center gap-2.5">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${done ? 'bg-green-100' : 'bg-gray-100'}`}>
                    {done ? <CheckCircle size={12} className="text-green-600" /> : <div className="w-2 h-2 rounded-full bg-gray-300" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-xs font-medium ${done ? 'text-gray-900' : 'text-gray-400'}`}>{step}</p>
                    {done && by && <p className="text-xs text-gray-500">{by}{date ? ` · ${date}` : ''}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            {record.status === 'pending_approval' && (
              <>
                <button onClick={() => { onApprove(record.id); onClose(); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-green-600 hover:bg-green-700 transition-colors">
                  <CheckCircle size={13} /> Approve
                </button>
                <button onClick={() => { onReject(record.id); onClose(); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-red-600 hover:bg-red-700 transition-colors">
                  <XCircle size={13} /> Reject
                </button>
              </>
            )}
            {record.status === 'approved' && record.variance !== 0 && (
              <button onClick={() => { onPostAdjustment(record.id); onClose(); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors" style={{ backgroundColor: 'var(--primary)' }}>
                <RefreshCw size={13} /> Post Adjustment
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BirdReconciliationContent() {
  const [records, setRecords] = useState<ReconciliationRecord[]>(MOCK_RECONCILIATIONS);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReconciliationStatus | 'all'>('all');
  const [farmFilter, setFarmFilter] = useState('all');
  const [showNewForm, setShowNewForm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ReconciliationRecord | null>(null);

  const nextNumber = `REC-2026-${String(records.length + 1).padStart(6, '0')}`;

  const filtered = records.filter(r => {
    const matchSearch = !search || [r.reconciliationNumber, r.farm, r.house, r.flock, r.flockNumber, r.birdType].some(v => v.toLowerCase().includes(search.toLowerCase()));
    const matchStatus = statusFilter === 'all' || r.status === statusFilter;
    const matchFarm = farmFilter === 'all' || r.farm === farmFilter;
    return matchSearch && matchStatus && matchFarm;
  });

  const stats = {
    total: records.length,
    pending: records.filter(r => r.status === 'pending_approval').length,
    approved: records.filter(r => r.status === 'approved').length,
    withVariance: records.filter(r => r.variance !== 0).length,
    posted: records.filter(r => r.status === 'adjustment_posted').length,
    totalVariance: records.reduce((s, r) => s + r.variance, 0),
  };

  const handleNew = (data: Partial<ReconciliationRecord>) => {
    const newR: ReconciliationRecord = {
      id: `rec-${Date.now()}`,
      reconciliationNumber: nextNumber,
      countDate: new Date().toISOString().split('T')[0],
      farm: data.farm || '',
      house: data.house || '',
      flock: data.flock || '',
      flockNumber: data.flockNumber || '',
      birdType: data.birdType || '',
      breed: data.breed || '',
      systemPopulation: data.systemPopulation || 0,
      physicalCount: data.physicalCount || 0,
      variance: data.variance || 0,
      variancePct: data.variancePct || 0,
      countedBy: data.countedBy || '',
      reason: data.reason,
      notes: data.notes,
      status: 'pending_approval',
      createdAt: new Date().toISOString(),
    };
    setRecords(p => [newR, ...p]);
  };

  const handleApprove = (id: string) => setRecords(p => p.map(r => r.id === id ? { ...r, status: 'approved', approvedBy: 'Farm Manager', verifiedBy: r.verifiedBy || 'Farm Manager' } : r));
  const handleReject = (id: string) => setRecords(p => p.map(r => r.id === id ? { ...r, status: 'rejected' } : r));
  const handlePostAdjustment = (id: string) => setRecords(p => p.map(r => r.id === id ? { ...r, status: 'adjustment_posted', adjustmentPosted: true } : r));

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Population Reconciliation</h1>
          <p className="text-xs text-gray-500 mt-0.5">Physical count vs system population — detect, investigate, and correct discrepancies</p>
        </div>
        <button onClick={() => setShowNewForm(true)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white rounded-lg transition-colors" style={{ backgroundColor: 'var(--primary)' }}>
          <Plus size={14} /> New Count
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total Counts', value: stats.total, color: '#2563eb', bg: '#dbeafe' },
          { label: 'Pending Approval', value: stats.pending, color: '#d97706', bg: '#fef3c7' },
          { label: 'Approved', value: stats.approved, color: '#16a34a', bg: '#dcfce7' },
          { label: 'With Variance', value: stats.withVariance, color: '#dc2626', bg: '#fee2e2' },
          { label: 'Adjustments Posted', value: stats.posted, color: '#7c3aed', bg: '#ede9fe' },
          { label: 'Net Variance', value: stats.totalVariance === 0 ? '0' : (stats.totalVariance > 0 ? '+' : '') + stats.totalVariance, color: stats.totalVariance < 0 ? '#dc2626' : stats.totalVariance > 0 ? '#d97706' : '#16a34a', bg: stats.totalVariance < 0 ? '#fee2e2' : stats.totalVariance > 0 ? '#fef3c7' : '#dcfce7' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className="p-3 rounded-xl border border-gray-100 shadow-sm bg-white">
            <p className="text-xs text-gray-500 truncate">{label}</p>
            <p className="text-xl font-semibold mt-1" style={{ color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Variance Alert Banner */}
      {stats.withVariance > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2.5 text-xs text-amber-800">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-amber-600" />
          <div>
            <p className="font-semibold">{stats.withVariance} reconciliation{stats.withVariance > 1 ? 's' : ''} with variance detected</p>
            <p className="mt-0.5 opacity-80">Review and approve pending reconciliations to maintain accurate population records. Do not auto-adjust — each variance requires investigation.</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="w-full pl-8 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30" placeholder="Search reconciliations..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white" value={statusFilter} onChange={e => setStatusFilter(e.target.value as ReconciliationStatus | 'all')}>
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 bg-white" value={farmFilter} onChange={e => setFarmFilter(e.target.value)}>
          <option value="all">All Farms</option>
          {FARMS.map(f => <option key={f}>{f}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {['Count #', 'Date', 'Farm / House', 'Flock', 'System', 'Physical', 'Variance', 'Variance %', 'Counted By', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left font-semibold text-gray-600 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-400">No reconciliation records found</td></tr>
              ) : filtered.map(r => {
                const cfg = STATUS_CONFIG[r.status];
                const StatusIcon = cfg.icon;
                const v = r.variance;
                return (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2.5 font-mono font-medium text-blue-700">{r.reconciliationNumber}</td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{r.countDate}</td>
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-gray-900 truncate max-w-[130px]">{r.farm}</p>
                      <p className="text-gray-500">{r.house}</p>
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-gray-900">{r.flockNumber}</p>
                      <p className="text-gray-500">{r.birdType}</p>
                    </td>
                    <td className="px-3 py-2.5 font-semibold text-blue-700">{r.systemPopulation.toLocaleString()}</td>
                    <td className="px-3 py-2.5 font-semibold text-green-700">{r.physicalCount.toLocaleString()}</td>
                    <td className="px-3 py-2.5">
                      {v === 0 ? (
                        <span className="flex items-center gap-1 text-green-600 font-medium"><Minus size={11} />None</span>
                      ) : v < 0 ? (
                        <span className="flex items-center gap-1 text-red-600 font-semibold"><TrendingDown size={11} />{v}</span>
                      ) : (
                        <span className="flex items-center gap-1 text-amber-600 font-semibold"><TrendingUp size={11} />+{v}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {v === 0 ? <span className="text-green-600">0.00%</span> : (
                        <span className={v < 0 ? 'text-red-600' : 'text-amber-600'}>{r.variancePct.toFixed(2)}%</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-gray-700">{r.countedBy}</td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ color: cfg.color, backgroundColor: cfg.bg }}>
                        <StatusIcon size={10} />{cfg.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelectedRecord(r)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors" title="View details">
                          <Eye size={13} />
                        </button>
                        {r.status === 'pending_approval' && (
                          <>
                            <button onClick={() => handleApprove(r.id)} className="p-1.5 rounded-lg hover:bg-green-50 text-green-600 transition-colors" title="Approve">
                              <CheckCircle size={13} />
                            </button>
                            <button onClick={() => handleReject(r.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600 transition-colors" title="Reject">
                              <XCircle size={13} />
                            </button>
                          </>
                        )}
                        {r.status === 'approved' && r.variance !== 0 && (
                          <button onClick={() => handlePostAdjustment(r.id)} className="p-1.5 rounded-lg hover:bg-purple-50 text-purple-600 transition-colors" title="Post adjustment">
                            <RefreshCw size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <p className="text-xs text-gray-500">Showing {filtered.length} of {records.length} reconciliation records</p>
        </div>
      </div>

      {/* Modals */}
      {showNewForm && <NewCountForm onClose={() => setShowNewForm(false)} onSubmit={handleNew} />}
      {selectedRecord && (
        <ReconciliationDetail
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onPostAdjustment={handlePostAdjustment}
        />
      )}
    </div>
  );
}
